/** landing-modes: gate (tier) evidence, stacked/integration targets and conflict waits for the UI. */
import type { Landing, LandingRecipeRun } from './types.js'

export interface GateCommandView {
  command: string
  /** Status of the matching recipe run, when the landing reported one. */
  status: LandingRecipeRun['status'] | 'unknown'
  label: string
}

const COMMAND_LABEL: Record<GateCommandView['status'], string> = {
  pending: 'Waiting',
  running: 'Running',
  pass: 'Passed',
  fail: 'Failed',
  reused: 'Already verified (reused)',
  unknown: 'Not run yet',
}

export interface GateView {
  /** "docs", or "default + core" for a tie. */
  tierName: string
  summary: string
  files: number
  strictness: number
  commands: GateCommandView[]
  reused: number
}

/** The tier that ran for a landing, each command with its recipe status ("reused" marked). */
export function gateView(l: Pick<Landing, 'gate' | 'recipe'>): GateView | undefined {
  const g = l.gate
  if (!g) return undefined
  const commands = g.commands.map((command): GateCommandView => {
    const run = l.recipe?.find((r) => r.command === command)
    const status = run?.status ?? 'unknown'
    return { command, status, label: COMMAND_LABEL[status] }
  })
  const reused = commands.filter((c) => c.status === 'reused').length
  const tierName = g.tiers.join(' + ') || 'default'
  const n = g.commands.length
  return {
    tierName,
    summary: `Gate: ${tierName} tier · ${n} command${n === 1 ? '' : 's'}${reused ? ` · ${reused} reused` : ''}`,
    files: g.files,
    strictness: g.strictness,
    commands,
    reused,
  }
}

export interface StackChild {
  landingId: string
  branch: string
  state: string
  label: string
  commit: string | null
}

export interface StackView {
  /** The integration branch. */
  integration: string
  /** Where the integration branch itself lands (its own landing's target), when known. */
  into?: string
  integrationLanding?: { landingId: string; state: string; label: string }
  children: StackChild[]
}

/** Human status of a landing ("waiting on conflict task", "Landed", "testing"…). */
export function landingLabel(l: Landing): string {
  if (l.waitingOn) return 'waiting on conflict task'
  if (l.state === 'landed') return 'landed'
  if (l.state === 'failed') return l.reason ?? 'failed'
  if (l.state === 'running') return l.step ?? 'running'
  return l.state.replace('_', ' ')
}

function newestByBranch(list: readonly Landing[]): Landing[] {
  const by = new Map<string, Landing>()
  for (const l of list) {
    if (!l.branch) continue
    const cur = by.get(l.branch)
    if (!cur || l.updatedAt > cur.updatedAt) by.set(l.branch, l)
  }
  return [...by.values()].sort((a, b) => a.createdAt - b.createdAt)
}

/**
 * The stack a landing belongs to: its integration branch (the landing's `targetBranch`, or its own
 * branch when it is the integration landing) with the newest landing per child branch.
 */
export function stackView(landings: readonly Landing[], l: Landing): StackView | undefined {
  const same = landings.filter((x) => x.projectId === l.projectId)
  const targeting = (branch: string) => same.filter((x) => x.targetBranch === branch)
  const integration =
    l.branch && targeting(l.branch).length
      ? l.branch
      : l.targetBranch && targeting(l.targetBranch).length
        ? l.targetBranch
        : undefined
  if (!integration) return undefined
  const children = newestByBranch(targeting(integration).filter((x) => x.branch !== integration))
  const own = newestByBranch(same.filter((x) => x.branch === integration))[0]
  return {
    integration,
    ...(own?.targetBranch ? { into: own.targetBranch } : {}),
    ...(own
      ? { integrationLanding: { landingId: own.id, state: own.state, label: landingLabel(own) } }
      : {}),
    children: children.map((c) => ({
      landingId: c.id,
      branch: c.branch ?? c.id,
      state: c.state,
      label: landingLabel(c),
      commit: c.commit,
    })),
  }
}

/** One-line note for the strip row: target, tier, conflict wait. */
export function landingModesNote(l: Landing | undefined): string | undefined {
  if (!l) return undefined
  const parts = [
    l.targetBranch ? `→ ${l.targetBranch}` : '',
    l.gate ? `${l.gate.tiers.join(' + ')} gate` : '',
    l.waitingOn ? 'waiting on conflict task' : '',
  ].filter(Boolean)
  return parts.length ? parts.join(' · ') : undefined
}
